@import '../../../global/reset';

:host {
	display: flow-root;
}

.viewport {
	position: relative;
}

.slides {
	--gap: 0.5rem;

	max-block-size: inherit;
	display: flex;
	gap: var(--gap);
	overflow: auto;
	scroll-snap-type: x mandatory;
	scroll-behavior: smooth;

	& > slot::slotted(*) {
		scroll-snap-align: center;
		max-inline-size: 100%;
		max-block-size:  100%;
	}
}

.arrows, .bullets, .autoplay-toggle {
	margin-block: 0.5rem;
	margin-inline: auto;
}

.arrows, .bullets {
	display: flex;
	align-items: center;
	justify-content: center;
	inline-size: fit-content;
}

.arrows button,
.autoplay-toggle {
	display: grid;
	place-content: center;
	color: $secondary;
	aspect-ratio: 1;
	inline-size: 38px;
	block-size:  38px;
	border-radius: 50%;
	border: 2px solid $primary; 

	svg {
		width: 10px;
	}

	&:not(:disabled):is(:hover, :focus-visible) {
		color: white;
		background-color: var(--bs-primary);
	}

	&:disabled {
		opacity: 0.25;
	}
}

.arrows {
	gap: 1rem;

	button {
		&.left {
			svg {
				margin-right: 3px;
			}
		}
		&.right {
			svg {
				margin-left: 3px;
			}
		}
	}
}

.bullets {
	gap: 0.75rem;
	font-family: $font-family-secondary;

	button {
		aspect-ratio: 1;
		inline-size: 24px;
		block-size:  24px;
		border-radius: 50%;
		display: grid;
		background-color: $primary;
		color: #fff;
		font-weight: 600;
		font-size: 18px;
		transition: all 300ms ease-out;
		display: flex;
		justify-content: center;
		align-items: center;
		padding-top: 2px;

		&:not(
			[aria-selected='true'],
			:hover, :focus-visible
		) {
			background-color: rgba($secondary, 0.2);
			color: $primary;
		}

		&[aria-selected='true'] {
			inline-size: 34px;
			block-size:  34px;
		}
	}
}

:host(.itineraries) {
	.controls-wrapper {
		position: relative;

		.arrows {
			width: 100%;
			justify-content: space-between;
		}
		.bullets {
			position: absolute;
			margin: 0;
			top: 0;
			left: 50%;
			transform: translateX(-50%);
			width: calc(100% - 4 * 38px);
			justify-content: space-around;
			height: 100%;
		}
	}
}

:host(:not(.with-scrollbar)) {
	.slides {
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}
	}
}
:host(.with-scrollbar) {
	.slides {
		padding-block-end: 40px;
		gap: 30px;

		&::-webkit-scrollbar-track {
            -webkit-box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.3);
            border-radius: 10px;
            background-color: #F6F4EF;
            margin-left: 50px;
            margin-right: 50px;
        }
    
        &::-webkit-scrollbar {
            height: 10px;
            margin-left: 50px;
            margin-right: 50px;
        }
    
        &::-webkit-scrollbar-thumb {
            border-radius: 10px;
            -webkit-box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.3);
            background: $secondary;
        }
	}
}

:host(.faded-edges) {
	--padding-inline: 4ch;
	--bg-color: white;

	.viewport {
		inline-size: calc(100% + 2 * var(--padding-inline));
		transform: translateX(calc(-1 * var(--padding-inline)));

		&::before, &::after {
			content: '';
			position: absolute;
			inset-block: 0;
			block-size: 100%;
			inline-size: var(--padding-inline);
		}

		&::before {
			z-index: 1;
			background-image: linear-gradient(to right, var(--bg-color), transparent);
			inset-inline-start: -1px;
		}

		&::after {
			background-image: linear-gradient(to left, var(--bg-color), transparent);
			inset-inline-end: -1px;
		}
	}

	.slides {
		scroll-padding-inline: var(--padding-inline);
	}

	.slides,
	.arrows,
	.bullets {
		padding-inline: var(--padding-inline);
	}
}

:host(.faded-edges.with-scrollbar) {
	.slides {
		&::-webkit-scrollbar-track {
			margin-inline: var(--padding-inline);
		}
	}
}

:host(.fixed-slide-count) {
	--count: 1;

	.slides > slot::slotted(*) {
		scroll-snap-align: start;
		flex-shrink: 0;
		inline-size: calc(
			(100% -
				(var(--count) - 1) * var(--gap)
			)
			/ var(--count)
		);
		min-inline-size: min-content;
	}
}

:host(.inverted) {
	.arrows button,
	.autoplay-toggle {
		color: white;
		background: none;

		&:not(:disabled):is(:hover, :focus-visible) {
			color: var(--bs-primary);
			background-color: white;
		}
	}

	.bullets {
		button {
			background-color: white;
		}
	}
}

:host(.scrolling-cards) {
	.controls-wrapper {
		margin-top: -90px;
		padding-bottom: 40px;
		padding-right: 50px;

		.arrows {
			width: 100%;
			justify-content: flex-end;
		}
	}
}

@include media-breakpoint-down(lg) {
	:host(.scrolling-cards) {
		.controls-wrapper {
			margin: 0 24px;
			padding-right: 0;
			padding-bottom: 20px;

			.arrows {
				justify-content: space-between;
			}
		}
	}
	:host(.with-scrollbar) {
		.slides {
			padding-block-end: 24px;
			gap: 15px;
	
			&::-webkit-scrollbar-track {
				margin-left: 24px;
				margin-right: 24px;
			}
		
			&::-webkit-scrollbar {
				margin-left: 24px;
				margin-right: 24px;
			}
		}
	}
	:host(.itineraries) {
		.controls-wrapper {	
			.bullets {
				width: calc(100% - 100px);

			}
		}
		.arrows {
			button {
				inline-size: 29px;
				block-size:  29px;
			}
		}
		.bullets{
			button {
				inline-size: 22px;
				block-size:  22px;	
				font-size: 16px;

				&[aria-selected='true'] {
					inline-size: 29px;
					block-size:  29px;
				}
			}
		}
	}
}